// ============================================================
// Site navigation — single source of truth for the header and footer.
//
// Adding a module is one line: drop an item into the right Product column.
// Every `href` must resolve to something real (navData.test.js enforces it):
//   • an SPA route declared in src/Routes.js            '/point-of-sale'
//   • an element id on that route                        '/#module-inv', '/pricing#estimate'
//   • a static build shipped in public/                  '/docs/…', '/demo/'
//   • a mailto: address
// Pages that exist in src/website but are not routed (Industries, Features,
// Solutions, Operations, InventoryManagement, BusinessIntelligence …) must not
// be linked until they get a route.
// ============================================================

import { CONTACT_EMAIL } from '../../constants/constants';

// Links that appear in more than one place are defined once.
export const LINKS = {
  modules: { label: 'All modules', href: '/#modules' },
  pos: { label: 'Point of Sale', href: '/point-of-sale' },
  store: { label: 'Online Store', href: '/online-store' },
  projects: { label: 'Project Management', href: '/project-management' },
  fleet: { label: 'Fleet Management', href: '/fleet-management' },
  ai: { label: 'Raya AI', href: '/ai' },
  pricing: { label: 'Pricing', href: '/pricing' },
  workflows: { label: 'Workflows', href: '/#workflows' },
  gst: { label: 'GST & compliance', href: '/#gst' },
  docs: { label: 'Docs', href: '/docs/' },
  demo: { label: 'Interactive demo', href: '/demo/' },
  faq: { label: 'FAQ', href: '/#faq' },
  contact: { label: 'Contact us', href: '/contact-us' },
  privacy: { label: 'Privacy notice & terms', href: '/privacy-policy' },
  privacyRequest: { label: 'Data requests (DPDP)', href: '/privacy-request' },
  cookies: { label: 'Cookie notice', href: '/privacy-policy#cookies' },
  login: { label: 'Log in', href: '/login' },
  register: { label: 'Start free', href: '/register' },
};

// ─── Top level ──────────────────────────────────────────────────────────────
// `kind`: 'mega' (full-width panel), 'dropdown' (narrow panel) or 'link'.
export const NAV = [
  {
    id: 'product',
    label: 'Product',
    kind: 'mega',
    columns: [
      {
        title: 'Run operations',
        items: [
          { label: 'Inventory', desc: 'Multi-warehouse stock, batches and reorder points', icon: 'package', href: '/#module-inv' },
          { label: 'Purchase', desc: 'Indent, PO, GRN and three-way matched bills', icon: 'shopping-cart', href: '/#module-pur' },
          { label: 'Manufacturing', desc: 'BOM, work orders, QC and stage tracking', icon: 'factory', href: '/#module-mfg' },
          { label: 'Fleet Management', desc: 'Trips, freight and hire bills, fuel and papers', icon: 'truck', href: '/fleet-management' },
          { label: 'Project Management', desc: 'Boards, timesheets and a project P&L', icon: 'folder-kanban', href: '/project-management' },
        ],
      },
      {
        title: 'Finance & people',
        items: [
          { label: 'Finance & Accounting', desc: 'Ledgers, bank reconciliation and P&L', icon: 'landmark', href: '/#module-fin' },
          { label: 'GST & e-Invoicing', desc: 'IRN, e-Way Bill and GSTR-ready data, built in', icon: 'file-text', href: '/#gst' },
          { label: 'HR & Payroll', desc: 'Attendance, leave, shifts and salary runs', icon: 'contact-round', href: '/#module-hr' },
        ],
      },
      {
        title: 'Sell & grow',
        items: [
          { label: 'Sales & Invoicing', desc: 'Order to cash, with IRN, QR and e-Way Bill', icon: 'receipt-indian-rupee', href: '/#workflows' },
          { label: 'Point of Sale', desc: 'Counter billing that posts straight to the books', icon: 'store', href: '/point-of-sale' },
          { label: 'Online Store', desc: 'Your branded web store on live ERP stock', icon: 'shopping-bag', href: '/online-store', badge: 'New' },
          { label: 'CRM & Marketing', desc: 'Leads, campaigns, follow-ups and quotes', icon: 'megaphone', href: '/#module-crm' },
          { label: 'Raya AI', desc: 'Drafts entries, reconciles and forecasts', icon: 'sparkles', href: '/ai' },
        ],
      },
      {
        title: 'Insights',
        items: [
          { label: 'Reports & dashboards', desc: 'Live KPIs, drill from chart to document', icon: 'bar-chart-3', href: '/#module-rep' },
          { label: 'Demand forecasts', desc: 'What will sell and what runs short, by AI', icon: 'line-chart', href: '/ai' },
        ],
      },
    ],
    footer: [
      { label: 'See all modules', href: LINKS.modules.href, icon: 'layers' },
      { label: 'What’s new: Online Store', href: LINKS.store.href, icon: 'sparkles' },
    ],
  },
  {
    id: 'solutions',
    label: 'Solutions',
    kind: 'mega',
    columns: [
      {
        title: 'By industry',
        wide: true,
        items: [
          { label: 'Manufacturing', desc: 'Shop floor to balance sheet', icon: 'factory', href: '/#industries' },
          { label: 'Trading & Distribution', desc: 'Stock in, invoices out, GST filed', icon: 'container', href: '/#industries' },
          { label: 'Retail & shops', desc: 'Raya POS at the counter, stock behind it', icon: 'store', href: '/point-of-sale' },
          { label: 'Logistics & Delivery', desc: 'Trips, freight bills and lorry-hire TDS', icon: 'truck', href: '/fleet-management' },
          { label: 'Jewellery', desc: 'Karigars, stones and custom orders', icon: 'gem', href: '/#industries' },
          { label: 'Healthcare & Pharmacy', desc: 'Medical stock, batches and billing', icon: 'stethoscope', href: '/#industries' },
          { label: 'Services', desc: 'Projects, timesheets and project margins', icon: 'wrench', href: '/project-management' },
          { label: 'Cafés & bakeries', desc: 'Touch-tile billing on a tablet', icon: 'coffee', href: '/point-of-sale' },
        ],
      },
      {
        title: 'By business size',
        items: [
          { label: 'Small business', desc: 'Pick your trade and see your monthly number', icon: 'user-check', href: '/pricing#estimate' },
          { label: 'Multi-branch & growing', desc: 'Every GSTIN under one PAN, many warehouses', icon: 'layers', href: '/#gst' },
          { label: 'Enterprise', desc: 'More than 30 users? Talk to us about bulk pricing', icon: 'building-2', href: '/contact-us' },
        ],
      },
    ],
  },
  { id: 'pricing', label: 'Pricing', kind: 'link', href: LINKS.pricing.href },
  {
    id: 'resources',
    label: 'Resources',
    kind: 'dropdown',
    // Pages that belong to this group for the "you are here" underline, even
    // though other menus link to them too (e.g. Enterprise → /contact-us).
    activeFor: ['/contact-us', '/privacy-policy', '/privacy-request'],
    columns: [
      {
        title: 'Learn and get help',
        items: [
          { label: 'Docs', desc: 'Guides for every module and workflow', icon: 'book-open', href: LINKS.docs.href },
          { label: 'Interactive demo', desc: 'Click through Raya ERP, no sign-up', icon: 'play-circle', href: LINKS.demo.href },
          { label: 'FAQ', desc: 'The questions people ask first', icon: 'help-circle', href: LINKS.faq.href },
          { label: 'Contact & support', desc: 'Talk to the team in Ahmedabad', icon: 'message-circle', href: LINKS.contact.href },
        ],
      },
    ],
  },
];

// Right-hand side of the header: exactly these three.
export const NAV_ACTIONS = {
  login: { label: 'Log in', href: LINKS.login.href },
  demo: { label: 'Book a demo', href: '/contact-us' },
  primary: { label: 'Start free', href: LINKS.register.href },
};

const byId = (id) => NAV.find((n) => n.id === id);
const column = (navId, title) => byId(navId).columns.find((c) => c.title === title);

// ─── Footer ─────────────────────────────────────────────────────────────────
// Built from the same items so the two never drift apart.
export const FOOTER_COLUMNS = [
  {
    title: 'Product',
    links: [LINKS.modules, LINKS.pos, LINKS.store, LINKS.projects, LINKS.fleet, LINKS.ai, LINKS.pricing, LINKS.workflows, LINKS.gst],
  },
  {
    title: 'Industries',
    links: column('solutions', 'By industry').items.map(({ label, href }) => ({ label, href })),
  },
  {
    title: 'Resources',
    links: byId('resources').columns[0].items.map(({ label, href }) => ({ label, href })),
  },
  {
    title: 'Company',
    links: [
      LINKS.contact, LINKS.login, LINKS.privacy, LINKS.cookies, LINKS.privacyRequest,
      // Not a link: opens the consent preferences so a choice can be changed or withdrawn.
      { label: 'Privacy choices', action: 'privacy-choices' },
      { label: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
    ],
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
// Static builds served next to the SPA; the router must not intercept them.
export const STATIC_PREFIXES = ['/docs/', '/demo/'];

export const isExternal = (href) =>
  /^(https?:|mailto:|tel:)/.test(href) || STATIC_PREFIXES.some((p) => href.startsWith(p));

export const splitHref = (href) => {
  const [path, hash = ''] = href.split('#');
  return { path: path || '/', hash };
};

// Every href in the header, its panels and the footer.
export const allNavHrefs = () => {
  const out = [];
  NAV.forEach((n) => {
    if (n.href) out.push(n.href);
    (n.columns || []).forEach((c) => c.items.forEach((i) => out.push(i.href)));
    (n.footer || []).forEach((f) => out.push(f.href));
  });
  Object.values(NAV_ACTIONS).forEach((a) => out.push(a.href));
  FOOTER_COLUMNS.forEach((c) => c.links.forEach((l) => l.href && out.push(l.href)));
  return out;
};
